import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ExternalLink, ImageOff, Loader2, RefreshCw, Search, X } from "lucide-react";
import { Sidebar } from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { CATEGORIES, CATEGORY_LABELS } from "../constants/categories";
import {
  listPendingEvents, updatePendingEvent, discardPendingEvents,
  approvePendingEvent, approvalBlocker, runScraperNow, getScraperStatus,
} from "../services/pendingEventsService";

// Nomes das fontes do scraper (ids de functions/scrapers/*.js).
const SOURCE_LABELS = {
  eticketcenter: "eTicketCenter",
  visitejoinville: "Visite Joinville",
  jllenet: "jlle.net",
};
// Intervalo de consulta enquanto uma busca roda no servidor.
const SEARCH_POLL_MS = 20000;

// Datas do contrato são strings "2026-10-18T20:00:00-03:00" (Brasília).
// Exibe lendo os números direto da string — sem new Date(), que aplicaria
// o fuso do navegador.
const WEEKDAYS = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];
const isoParts = (iso) => {
  const m = typeof iso === "string" && iso.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  return m ? { y: +m[1], mo: +m[2], d: +m[3], h: m[4], mi: m[5] } : null;
};
const formatEventDate = (startIso, endIso) => {
  const s = isoParts(startIso);
  if (!s) return "-";
  const weekday = WEEKDAYS[new Date(Date.UTC(s.y, s.mo - 1, s.d)).getUTCDay()];
  const date = `${weekday}, ${String(s.d).padStart(2, "0")}/${String(s.mo).padStart(2, "0")}/${s.y}`;
  const e = isoParts(endIso);
  const sameDay = e && e.y === s.y && e.mo === s.mo && e.d === s.d;
  const end = e ? (sameDay ? `–${e.h}:${e.mi}` : ` até ${String(e.d).padStart(2, "0")}/${String(e.mo).padStart(2, "0")} ${e.h}:${e.mi}`) : "";
  return `${date} · ${s.h}:${s.mi}${end}`;
};
const formatPrice = (event) => {
  if (event.isFree) return "Gratuito";
  if (event.ticketPrice === null || event.ticketPrice === undefined) return "Preço a definir";
  return Number(event.ticketPrice).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};

// <input type="datetime-local"> ↔ string do contrato com -03:00.
const toInputValue = (iso) => (typeof iso === "string" ? iso.slice(0, 16) : "");
const fromInputValue = (value) => (value ? `${value}:00-03:00` : "");

// Grupos recolhidos ({ review: true, ready: false }) — preferência só deste
// navegador. Sem nada salvo (ou storage bloqueado), os dois começam abertos.
const COLLAPSED_GROUPS_KEY = "joyn_admin_pending_collapsed";

const loadCollapsedGroups = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(COLLAPSED_GROUPS_KEY));
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const saveCollapsedGroups = (value) => {
  try {
    localStorage.setItem(COLLAPSED_GROUPS_KEY, JSON.stringify(value));
  } catch {
    // storage bloqueado — a escolha vale só nesta visita
  }
};

// Grupo de cada pendente — mesma divisão da lista (needsReview).
const groupKeyOf = (item) => (item.needsReview ? "review" : "ready");

export const AdminPendingEvents = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [editing, setEditing] = useState(null);
  const [searching, setSearching] = useState(false);
  const [searchSummary, setSearchSummary] = useState(null);
  const [collapsedGroups, setCollapsedGroups] = useState(loadCollapsedGroups);
  const pollRef = useRef(null);

  const toggleGroup = (key) => {
    setCollapsedGroups((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      saveCollapsedGroups(next);
      return next;
    });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await listPendingEvents();
      setItems(list);
      setSelected((prev) => new Set([...prev].filter((id) => list.some((i) => i.id === id))));
    } catch (e) {
      toast.error("Erro ao carregar pendentes: " + e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ---- "Buscar agora" ----
  const stopPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = null;
  };

  const finishSearch = useCallback(async (summary) => {
    stopPolling();
    setSearching(false);
    if (summary) setSearchSummary(summary);
    await load();
    window.dispatchEvent(new Event("pending-events-changed"));
  }, [load]);

  // Acompanha uma busca que roda no servidor (disparada aqui antes de a
  // conexão cair, por outra aba, pela busca agendada ou pelo endpoint).
  const startPolling = useCallback(() => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const status = await getScraperStatus();
        if (!status.running) finishSearch(status.lastRun?.summary || null);
      } catch {
        // Falha pontual de rede: tenta de novo no próximo intervalo.
      }
    }, SEARCH_POLL_MS);
  }, [finishSearch]);

  // Ao abrir a aba, se já houver busca rodando, mostra "Buscando...".
  useEffect(() => {
    let cancelled = false;
    getScraperStatus()
      .then((status) => {
        if (!cancelled && status.running) {
          setSearching(true);
          startPolling();
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [startPolling]);

  const handleSearchNow = async () => {
    setSearching(true);
    setSearchSummary(null);
    try {
      const result = await runScraperNow();
      if (result.status === "busy") {
        toast.info("Busca já em andamento — o resultado aparece aqui quando terminar.");
        startPolling();
        return;
      }
      await finishSearch(result.summary);
    } catch (e) {
      if (e.code === "functions/permission-denied" || e.code === "functions/unauthenticated") {
        toast.error("Sem permissão para buscar: " + e.message);
        setSearching(false);
        return;
      }
      // Conexão caiu ou tempo esgotado no navegador: a busca segue no
      // servidor (com a trava), então só acompanhamos até terminar.
      toast.info("A busca continua no servidor — o resultado aparece aqui quando terminar.");
      startPolling();
    }
  };

  const removeFromList = (ids) => {
    const gone = new Set(ids);
    setItems((prev) => prev.filter((i) => !gone.has(i.id)));
    setSelected((prev) => new Set([...prev].filter((id) => !gone.has(id))));
    window.dispatchEvent(new Event("pending-events-changed"));
  };

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // Só eventos de grupos abertos podem ficar selecionados — nada escondido
  // é aprovado/descartado sem querer. O efeito desmarca o que sair de vista
  // (grupo recolhido, ou evento que mudou de grupo após edição).
  const visibleItems = items.filter((i) => !collapsedGroups[groupKeyOf(i)]);

  useEffect(() => {
    const visibleIds = new Set(
      items.filter((i) => !collapsedGroups[groupKeyOf(i)]).map((i) => i.id)
    );
    setSelected((prev) => {
      const next = new Set([...prev].filter((id) => visibleIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [items, collapsedGroups]);

  const allSelected = visibleItems.length > 0 && visibleItems.every((i) => selected.has(i.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(visibleItems.map((i) => i.id)));

  const approveMany = async (targets) => {
    const flagged = targets.filter((t) => t.needsReview).length;
    if (flagged > 0 && !window.confirm(
      `${flagged} evento(s) estão marcados como "Revisar". Aprovar mesmo assim?`
    )) return;

    setBusy(true);
    const approved = [];
    const failures = [];
    for (const item of targets) {
      try {
        await approvePendingEvent(item);
        approved.push(item.id);
      } catch (e) {
        failures.push(`"${item.event.title}" (${e.message})`);
      }
    }
    removeFromList(approved);
    setBusy(false);

    if (approved.length) toast.success(`${approved.length} evento(s) publicado(s)`);
    if (failures.length) {
      toast.error(`${failures.length} não aprovado(s): ${failures.join("; ")}`, { duration: 15000 });
    }
  };

  const discardMany = async (targets) => {
    if (!window.confirm(
      targets.length === 1
        ? `Descartar "${targets[0].event.title}"? Ele não volta nas próximas buscas.`
        : `Descartar ${targets.length} eventos? Eles não voltam nas próximas buscas.`
    )) return;

    setBusy(true);
    try {
      const ids = targets.map((t) => t.id);
      await discardPendingEvents(ids);
      removeFromList(ids);
      toast.success(`${ids.length} evento(s) descartado(s)`);
    } catch (e) {
      toast.error("Erro ao descartar: " + e.message);
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async (form) => {
    setBusy(true);
    try {
      const event = {
        ...form,
        startDate: fromInputValue(form.startDate),
        endDate: fromInputValue(form.endDate),
        ticketPrice: form.isFree ? 0 : (form.ticketPrice === "" ? null : Number(form.ticketPrice)),
      };
      await updatePendingEvent(editing.id, event);
      setEditing(null);
      toast.success("Alterações salvas");
      await load();
    } catch (e) {
      toast.error("Erro ao salvar: " + e.message);
    } finally {
      setBusy(false);
    }
  };

  const selectedItems = items.filter((i) => selected.has(i.id));

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />

      <main className="flex-1 min-w-0 overflow-x-clip p-3 pt-[4.5rem] sm:p-4 sm:pt-[4.5rem] md:p-6 md:ml-72 space-y-4">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold truncate">Eventos Pendentes</h1>
            <span className="shrink-0 text-xs sm:text-sm font-semibold bg-accent text-accent-foreground rounded-full px-2.5 py-0.5">
              {items.length}
            </span>
          </div>
          <div className="flex gap-2 ml-auto shrink-0">
            <Button
              variant="outline"
              size="sm"
              className="h-9 px-2.5 sm:px-3"
              onClick={load}
              disabled={loading || busy}
              aria-label="Atualizar"
            >
              <RefreshCw className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Atualizar</span>
            </Button>
            <Button
              size="sm"
              className="h-9 px-3"
              onClick={handleSearchNow}
              disabled={searching || busy}
            >
              {searching ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Buscando...</>
              ) : (
                <><Search className="w-4 h-4 mr-2" /> Buscar<span className="hidden sm:inline">&nbsp;agora</span></>
              )}
            </Button>
          </div>
        </div>

        {searching && (
          <p className="text-xs sm:text-sm rounded-lg border bg-muted/50 px-3 py-2">
            Buscando eventos em todas as fontes. Pode levar alguns minutos — você pode
            sair desta página, a busca continua no servidor.
          </p>
        )}

        {searchSummary && (
          <SearchSummary summary={searchSummary} onClose={() => setSearchSummary(null)} />
        )}

        <p className="text-xs sm:text-sm text-muted-foreground">
          Encontrados automaticamente pela busca de segunda e quinta (4h). Aprovar publica no app
          pelo mesmo fluxo do importador JSON, incluindo o envio da imagem para o Storage.
        </p>

        {items.length > 0 && (
          <div className="sticky top-14 md:top-0 z-30 border rounded-xl p-2.5 sm:p-3 bg-card/95 backdrop-blur shadow-sm flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
            <div className="flex items-center justify-between sm:justify-start gap-3">
              <label className="flex items-center gap-2 text-sm cursor-pointer min-h-[2.25rem]">
                <Checkbox checked={allSelected} onCheckedChange={toggleAll} className="h-5 w-5" />
                Selecionar todos
              </label>
              <span className="text-xs sm:text-sm text-muted-foreground">{selected.size} selecionado(s)</span>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:flex sm:ml-auto">
              <Button size="sm" className="h-10 sm:h-9" disabled={!selected.size || busy} onClick={() => approveMany(selectedItems)}>
                Aprovar<span className="hidden sm:inline">&nbsp;selecionados</span>
              </Button>
              <Button size="sm" variant="destructive" className="h-10 sm:h-9" disabled={!selected.size || busy} onClick={() => discardMany(selectedItems)}>
                Descartar<span className="hidden sm:inline">&nbsp;selecionados</span>
              </Button>
            </div>
          </div>
        )}

        {loading ? (
          <p className="text-sm">Carregando...</p>
        ) : items.length === 0 ? (
          <p className="text-sm">Nenhum evento aguardando aprovação 🎉</p>
        ) : (
          // Dois grupos pelo mesmo needsReview que liga o selo "Revisar" e
          // o aviso ao aprovar; filter mantém a ordem original em cada um.
          [
            { key: "review", title: "Precisam de revisão", list: items.filter((i) => i.needsReview) },
            { key: "ready", title: "Prontos para aprovar", list: items.filter((i) => !i.needsReview) },
          ]
            .filter((group) => group.list.length > 0)
            .map((group) => {
              const collapsed = Boolean(collapsedGroups[group.key]);
              return (
                <section key={group.key} className="space-y-2">
                  <h2 className="text-base sm:text-lg font-semibold">
                    <button
                      type="button"
                      onClick={() => toggleGroup(group.key)}
                      aria-expanded={!collapsed}
                      aria-controls={`pending-group-${group.key}`}
                      className="inline-flex items-center gap-1.5 min-h-[2.25rem] rounded-md hover:text-primary transition-colors"
                    >
                      <ChevronDown
                        className={`w-4 h-4 sm:w-5 sm:h-5 shrink-0 transition-transform duration-200 ${collapsed ? "-rotate-90" : ""}`}
                      />
                      {group.title} ({group.list.length})
                    </button>
                  </h2>
                  {!collapsed && (
                    <div id={`pending-group-${group.key}`} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3">
                      {group.list.map((item) => (
                        <PendingCard
                          key={item.id}
                          item={item}
                          checked={selected.has(item.id)}
                          busy={busy}
                          onToggle={() => toggle(item.id)}
                          onApprove={() => approveMany([item])}
                          onEdit={() => setEditing(item)}
                          onDiscard={() => discardMany([item])}
                        />
                      ))}
                    </div>
                  )}
                </section>
              );
            })
        )}
      </main>

      {editing && (
        <EditDialog
          item={editing}
          busy={busy}
          onClose={() => setEditing(null)}
          onSave={saveEdit}
        />
      )}
    </div>
  );
};

// Resumo da última busca: totais + linha por fonte.
const SearchSummary = ({ summary, onClose }) => {
  const sources = Object.entries(summary.sources || {});
  const total = (key) => sources.reduce((acc, [, s]) => acc + (s[key] || 0), 0);
  const errors = total("erros");

  return (
    <div className="relative rounded-xl border bg-card p-3 sm:p-4 pr-10 text-sm shadow-sm">
      <button
        type="button"
        onClick={onClose}
        className="absolute right-2 top-2 p-1.5 rounded-md hover:bg-accent"
        aria-label="Fechar resumo"
      >
        <X className="w-4 h-4" />
      </button>
      <p className="font-semibold">
        Busca concluída: {summary.totalSaved || 0} novo(s) · {total("duplicados")} repetido(s)
        {errors > 0 && <span className="text-red-600"> · {errors} erro(s)</span>}
      </p>
      {summary.fatal && <p className="text-red-600 text-xs mt-1">{summary.fatal}</p>}
      <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
        {sources.map(([id, s]) => (
          <li key={id} className="break-words">
            <span className="font-medium text-foreground">{SOURCE_LABELS[id] || id}:</span>{" "}
            {s.salvos || 0} novo(s) · {s.duplicados || 0} repetido(s)
            {s.descartadosIA > 0 && ` · ${s.descartadosIA} descartado(s) pela IA`}
            {" · "}
            <span className={s.erros > 0 ? "text-red-600" : ""}>{s.erros || 0} erro(s)</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const PendingCard = ({ item, checked, busy, onToggle, onApprove, onEdit, onDiscard }) => {
  const { event } = item;
  const blocker = approvalBlocker(event);

  return (
    <div className={`min-w-0 border rounded-lg bg-card shadow-sm overflow-hidden flex flex-col ${checked ? "ring-2 ring-primary" : ""}`}>
      {/* Só exibição: 16:9 com object-cover, mesmo corte que o app mostra.
          A imagem salva na aprovação continua a original, sem recorte. */}
      <div className="relative aspect-video bg-muted">
        {event.imageUrl ? (
          <img src={event.imageUrl} alt={event.title} className="absolute inset-0 w-full h-full object-cover" loading="lazy" referrerPolicy="no-referrer" />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground text-xs gap-1">
            <ImageOff className="w-5 h-5" /> Sem imagem
          </div>
        )}
        <label className="absolute top-1.5 left-1.5 h-9 w-9 flex items-center justify-center bg-background/90 rounded-md cursor-pointer">
          <Checkbox checked={checked} onCheckedChange={onToggle} aria-label="Selecionar" className="h-5 w-5" />
        </label>
        {item.needsReview && (
          <span className="absolute top-2 right-2 text-[10px] bg-amber-100 text-amber-800 border border-amber-300 rounded px-1.5 py-0.5 font-bold uppercase">
            Revisar
          </span>
        )}
      </div>

      <div className="p-2.5 sm:p-3 flex-1 flex flex-col gap-0.5 min-w-0">
        <p className="text-sm font-semibold leading-snug line-clamp-2 break-words">{event.title}</p>
        <p className="text-[11px] sm:text-xs text-muted-foreground">📅 {formatEventDate(event.startDate, event.endDate)}</p>
        <p className="text-[11px] sm:text-xs text-muted-foreground line-clamp-2 break-words" title={event.location}>
          📍 {event.location || "Endereço não informado"}
        </p>
        <p className="text-[11px] sm:text-xs text-muted-foreground">
          🎟️ {formatPrice(event)} · {CATEGORY_LABELS[event.category] || event.category}
        </p>
        <a
          href={item.sourceUrl}
          target="_blank"
          rel="noreferrer"
          className="text-[11px] sm:text-xs text-primary inline-flex items-center gap-1 hover:underline self-start py-0.5"
        >
          Fonte: {item.sourceLabel || item.source} <ExternalLink className="w-3 h-3" />
        </a>

        <details className="text-[11px] sm:text-xs">
          <summary className="cursor-pointer text-muted-foreground py-0.5">Descrição</summary>
          <p className="mt-1 whitespace-pre-line break-words">{event.description}</p>
        </details>

        {item.reviewNotes?.length > 0 && (
          <ul className={`text-[11px] list-disc pl-4 mt-0.5 space-y-0.5 break-words ${
            item.needsReview ? "text-amber-700 dark:text-amber-400" : "text-muted-foreground"
          }`}>
            {item.reviewNotes.map((note) => <li key={note} className="[overflow-wrap:anywhere]">{note}</li>)}
          </ul>
        )}
      </div>

      <div className="p-2.5 sm:p-3 pt-0 sm:pt-0 grid grid-cols-3 gap-1.5">
        <Button size="sm" className="h-10 sm:h-8 px-1 text-xs" disabled={busy || !!blocker} title={blocker || ""} onClick={onApprove}>
          Aprovar
        </Button>
        <Button size="sm" variant="outline" className="h-10 sm:h-8 px-1 text-xs" disabled={busy} onClick={onEdit}>
          Editar
        </Button>
        <Button size="sm" variant="outline" className="h-10 sm:h-8 px-1 text-xs text-red-600" disabled={busy} onClick={onDiscard}>
          Descartar
        </Button>
      </div>
    </div>
  );
};

const EditDialog = ({ item, busy, onClose, onSave }) => {
  const { event } = item;
  const [form, setForm] = useState({
    title: event.title || "",
    description: event.description || "",
    startDate: toInputValue(event.startDate),
    endDate: toInputValue(event.endDate),
    location: event.location || "",
    category: event.category || "outros",
    isFree: !!event.isFree,
    ticketPrice: event.ticketPrice ?? "",
    ticketUrl: event.ticketUrl || "",
    imageUrl: event.imageUrl || "",
  });
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e?.target ? e.target.value : e }));

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-[calc(100vw-1.5rem)] max-w-2xl max-h-[90dvh] overflow-y-auto overflow-x-hidden p-4 sm:p-6 gap-3 rounded-lg">
        <DialogHeader>
          <DialogTitle>Editar antes de aprovar</DialogTitle>
          <DialogDescription>Horários sempre no fuso de Brasília.</DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 min-w-0">
          <Field label="Título"><Input value={form.title} onChange={set("title")} /></Field>
          <Field label="Descrição">
            <Textarea rows={5} value={form.description} onChange={set("description")} />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Início"><Input type="datetime-local" className="min-w-0 w-full" value={form.startDate} onChange={set("startDate")} /></Field>
            <Field label="Término"><Input type="datetime-local" className="min-w-0 w-full" value={form.endDate} onChange={set("endDate")} /></Field>
          </div>
          <Field label="Local (Rua X, N, Bairro, Joinville - SC (Local))">
            <Input value={form.location} onChange={set("location")} />
          </Field>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 items-end">
            <Field label="Categoria">
              <Select value={form.category} onValueChange={set("category")}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <label className="flex items-center gap-2 text-sm h-10 cursor-pointer">
              <Switch checked={form.isFree} onCheckedChange={set("isFree")} /> Gratuito
            </label>
            <Field label="Preço (R$)" className="col-span-2 sm:col-span-1">
              <Input type="number" min="0" step="0.01" disabled={form.isFree} value={form.isFree ? 0 : form.ticketPrice} onChange={set("ticketPrice")} />
            </Field>
          </div>
          <Field label="Link de ingresso"><Input value={form.ticketUrl} onChange={set("ticketUrl")} /></Field>
          <Field label="URL da imagem (oficial, em alta)">
            <Input value={form.imageUrl} onChange={set("imageUrl")} />
          </Field>
          {form.imageUrl && (
            <div className="grid gap-1">
              <span className="text-xs text-muted-foreground">Como aparece no app (16:9) — só visualização, a imagem salva é a original</span>
              <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-muted">
                <img src={form.imageUrl} alt="" className="absolute inset-0 w-full h-full object-cover" referrerPolicy="no-referrer" />
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="sticky bottom-0 -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 bg-background border-t grid grid-cols-2 gap-2 sm:flex sm:space-x-0">
          <Button variant="outline" className="h-10" onClick={onClose} disabled={busy}>Cancelar</Button>
          <Button className="h-10" onClick={() => onSave(form)} disabled={busy}>{busy ? "Salvando..." : "Salvar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, children, className = "" }) => (
  <div className={`grid gap-1.5 min-w-0 ${className}`}>
    <Label className="text-xs">{label}</Label>
    {children}
  </div>
);
